import { useState, useEffect, useCallback, useRef } from "react";
import { Table } from "../../../components";
import axios from "axios";
import { MapPin, Banknote, Eye } from "lucide-react";
import { GET_DEBTORS_URL } from "../../../features/UrlHelper";
import { useOrdersRealtime } from "../../../context/RealtimeContext";
import DebtHistoryModal from "./DebtHistoryModal";
import DebtPaymentModal from "./DebtPaymentModal";
import BulkDebtPaymentModal from "./BulkDebtPaymentModal";
import "./DebtManagementSection.scss";

function DebtManagementSection() {
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentCustomer, setPaymentCustomer] = useState(null);
  const [selectedDebtors, setSelectedDebtors] = useState([]); // IDs of selected debtors
  const [bulkPaymentDebtors, setBulkPaymentDebtors] = useState(null); // For bulk payment modal

  const handleRowClick = (row) => {
    if (window.innerWidth >= 768) {
      setSelectedCustomer(row);
    }
  };

  const handleSelectDebtor = (e, debtorId) => {
    e.stopPropagation();
    setSelectedDebtors((prev) =>
      prev.includes(debtorId) ? prev.filter((id) => id !== debtorId) : [...prev, debtorId]
    );
  };

  const handleSelectAll = (e) => {
    e.stopPropagation();
    if (selectedDebtors.length === debtors.length) {
      setSelectedDebtors([]);
    } else {
      setSelectedDebtors(debtors.map((d) => d.id));
    }
  };

  const handleBulkPayment = () => {
    const bulkDebtors = debtors.filter((d) => selectedDebtors.includes(d.id));
    setBulkPaymentDebtors(bulkDebtors);
  };

  useEffect(() => {
    loadDebtors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debtorsSeqRef = useRef(0);

  async function loadDebtors({ silent = false } = {}) {
    const seq = ++debtorsSeqRef.current;
    try {
      const { data } = await axios.get(GET_DEBTORS_URL);
      if (seq !== debtorsSeqRef.current) return;
      setDebtors(data);
    } catch (error) {
      console.error("Chyba pri načítaní dlžníkov:", error);
    } finally {
      if (!silent && seq === debtorsSeqRef.current) setLoading(false);
    }
  }

  // ─── Realtime auto-refresh ──────────────────────────────────────────────────
  // Odklad, kým je otvorený ktorýkoľvek modál: selectedDebtors drží zaškrtnuté
  // ID a refetch, ktorý zaškrtnutého dlžníka odstráni, by nechal zastaraný
  // výber vstupovať do handleBulkPayment.
  const modalOpen = !!selectedCustomer || !!paymentCustomer || !!bulkPaymentDebtors;
  const modalOpenRef = useRef(modalOpen);
  modalOpenRef.current = modalOpen;
  const pendingRefetchRef = useRef(false);

  const refetchDebtors = useCallback(() => {
    if (modalOpenRef.current) {
      pendingRefetchRef.current = true;
      return;
    }
    loadDebtors({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!modalOpen && pendingRefetchRef.current) {
      pendingRefetchRef.current = false;
      loadDebtors({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  // Široký filter zámerne: saldom zákazníka pohne prakticky ktorákoľvek
  // udalosť o objednávke -- doručenie typu DEBT, korekcia platby vodičom aj
  // platba dlhu. Dátum ani trasa tu nič nefiltrujú.
  useOrdersRealtime({
    filter: (evt) => evt.entity !== "route",
    onChange: refetchDebtors,
    debounceMs: 1000,
  });

  return (
    <div className="debt">
      <div className="debt__header">
        <h1 className="debt__title">Správa dlhov</h1>
        {selectedDebtors.length > 0 && (
          <button
            className="debt__bulk-pay-btn"
            onClick={handleBulkPayment}
            title={`Vyplatiť dlhy ${selectedDebtors.length} vybratým dlžníkom`}
          >
            <Banknote size={16} />
            <span>Vyplatiť vybrané ({selectedDebtors.length})</span>
          </button>
        )}
      </div>

      <Table
        columns={[
          {
            key: "select",
            label: (
              <div onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedDebtors.length === debtors.length && debtors.length > 0}
                  onChange={handleSelectAll}
                  title="Vybrať všetko"
                  className="debt__checkbox"
                />
              </div>
            ),
            sortable: false,
            width: "50px",
            render: (_, row) => (
              <div onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedDebtors.includes(row.id)}
                  onChange={(e) => handleSelectDebtor(e, row.id)}
                  className="debt__checkbox"
                />
              </div>
            ),
          },
          { key: "firstname", label: "Meno" },
          { key: "lastname", label: "Priezvisko" },
          { key: "phone", label: "Telefón" },
          {
            key: "addresses",
            label: "Adresy",
            render: (addresses) =>
              Array.isArray(addresses) && addresses.length > 0 ? (
                <div className="debt__addresses">
                  {addresses.map((a, i) => (
                    <div key={i} className="debt__address-row">
                      <MapPin className="debt__address-icon" size={13} />
                      <span>
                        {a.address_code ? (
                          <strong className="debt__address-code">{a.address_code}</strong>
                        ) : null}
                        {a.address_code && a.address ? " – " : ""}
                        {a.address}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="debt__no-address">—</span>
              ),
          },
          {
            key: "actual_saldo",
            label: (
              <div className="header-debt">
                <div>Aktuálny</div>
                <div>dlh</div>
              </div>
            ),
            className: "sticky-debt-column",
            render: (val) => (
              <span className="debt__amount sticky-debt-column">
                {Number(val).toFixed(2).replace(".", ",")} €
              </span>
            ),
          },
          {
            key: "actions",
            label: "Akcie",
            sortable: false,
            className: "desktop-only-action",
            render: (_, row) => (
              <button
                className="debt__pay-btn"
                onClick={(e) => { e.stopPropagation(); setPaymentCustomer(row); }}
                title="Prijať platbu"
              >
                <Banknote size={14} />
                <span>Prijať platbu</span>
              </button>
            ),
          },
        ]}
        data={debtors}
        onRowClick={handleRowClick}
        mobileRowActionsRender={(row) => (
          <div className="mobile-actions-wrapper">
            <button
              className="debt__mobile-detail-btn"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCustomer(row);
              }}
              title="Zobraziť históriu dlhu"
            >
              <Eye size={16} />
              <span>Detail</span>
            </button>
            <button
              className="debt__pay-btn"
              onClick={(e) => {
                e.stopPropagation();
                setPaymentCustomer(row);
              }}
              title="Prijať platbu"
            >
              <Banknote size={16} />
              <span>Prijať platbu</span>
            </button>
          </div>
        )}
        searchPlaceholder="Hľadať zákazníka..."
        emptyMessage={loading ? "Načítavam..." : "Žiadni dlžníci"}
      />

      {selectedCustomer && (
        <DebtHistoryModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onOpenPayment={() => {
            setPaymentCustomer(selectedCustomer);
            setSelectedCustomer(null);
          }}
        />
      )}

      {paymentCustomer && (
        <DebtPaymentModal
          customer={paymentCustomer}
          onClose={() => setPaymentCustomer(null)}
          onSuccess={() => {
            setPaymentCustomer(null);
            alert("Platba bola úspešne zaevidovaná.");
            setSelectedDebtors([]);
            loadDebtors();
          }}
        />
      )}

      {bulkPaymentDebtors && (
        <BulkDebtPaymentModal
          debtors={bulkPaymentDebtors}
          onClose={() => setBulkPaymentDebtors(null)}
          onSuccess={() => {
            setBulkPaymentDebtors(null);
            setSelectedDebtors([]);
            alert("Hromadná platba bola úspešne zaevidovaná.");
            loadDebtors();
          }}
        />
      )}
    </div>
  );
}

export default DebtManagementSection;
